<form
  [formGroup]="form"
  NfFormValidation
  (validSubmit)="onSave()"
  data-cy="bulk-form"
>
  <div class="modal-header" hsDraggable="modal-dialog">
    <h4 class="modal-title">{{ modalTitle }}</h4>
    <button
      nf-icon-button="cancel"
      icon="close"
      class="btn-bare"
      ngbTooltip="Close"
      (click)="onCancel()"
    ></button>
  </div>

  <div class="modal-body" hsDraggable="modal-dialog">
    <ng-container *ngIf="isHold">
      <div class="expires-row" data-cy="expires-section">
        <div class="expires-col">
          <div class="control-label expires-heading">Expires</div>
          <nf-toggle
            class="segment-toggle"
            data-cy="expires-toggle"
            [states]="expiresToggleStates"
            formControlName="expiresToggle"
          ></nf-toggle>
        </div>
        <nf-form-control
          class="expires-value"
          label="Expires in business days"
          compact
          *ngIf="!expiresToggle.value"
          [customErrorMessages]="validationMessages.holdTimeValidationMessages"
        >
          <input
            min="1"
            type="number"
            class="form-control"
            formControlName="expiresInDays"
            data-cy="expires-in-days"
          />
        </nf-form-control>
        <nf-form-control
          class="expires-value"
          label="Expire when reconcile on date"
          compact
          *ngIf="expiresToggle.value"
          [customErrorMessages]="validationMessages.holdTimeValidationMessages"
        >
          <nf-datepicker
            formControlName="expiresOnDate"
            [format]="dateFormat"
            [placeholder]="dateFormat"
            data-cy="expires-on-date"
          ></nf-datepicker>
        </nf-form-control>
      </div>
    </ng-container>

    <div class="toggle-row" [class.cautions-below]="hasCautions">
      <div class="toggle-block">
        <div class="control-label">Existing codes</div>
        <nf-toggle
          class="segment-toggle"
          data-cy="keep-toggle"
          [states]="{ on: 'Override', off: 'Keep' }"
          [ngModel]="keepControl.value === 'override'"
          [ngModelOptions]="{ standalone: true }"
          (ngModelChange)="keepControl.setValue($event ? 'override' : 'keep')"
        ></nf-toggle>
      </div>
      <div class="toggle-block">
        <div class="control-label">Note handling</div>
        <nf-toggle
          class="segment-toggle"
          data-cy="note-handling-toggle"
          [states]="{ on: 'Append', off: 'Override' }"
          [ngModel]="noteHandlingControl.value === 'append'"
          [ngModelOptions]="{ standalone: true }"
          (ngModelChange)="
            noteHandlingControl.setValue($event ? 'append' : 'override')
          "
        ></nf-toggle>
      </div>
      <button
        nf-icon-button="secondary"
        icon="content-paste"
        class="btn-bare paste-break-codes-btn"
        type="button"
        data-cy="paste-break-codes"
        [disabled]="!canPaste"
        ngbTooltip="Paste Break Codes (Alt+V)"
        (click)="onPasteBreakCodes()"
      ></button>
    </div>

    <div
      *ngIf="hasCautions"
      class="dialog-caution"
      data-cy="keep-blocked-codes-caution"
    >
      Your codes won't apply to {{ keepBlockedCodeCount }} break{{
        keepBlockedCodeCount === 1 ? '' : 's'
      }}
      with a different existing code. Switch to Override to replace them.
    </div>

    <div class="quick-apply" data-cy="quick-apply">
      <div class="quick-apply-title">Quick Apply to All</div>
      <div class="field-row">
        <nf-form-control
          *ngIf="quickApplyActionOptions().length > 0"
          label="Action"
          compact
        >
          <ng-select
            [items]="quickApplyActionOptions()"
            placeholder="Apply action to all groups"
            formControlName="quickApplyAction"
            [closeOnSelect]="true"
            data-cy="quick-apply-action"
            (change)="applyQuickAction($event)"
          >
            <ng-template ng-label-tmp let-item="item">
              {{ item
              }}<span
                *ngIf="quickApplyNotesRequiredSet.has(item)"
                class="notes-required-diamond"
              >
                ◆</span
              >
            </ng-template>
            <ng-template ng-option-tmp let-item="item">
              <div [ngbTooltip]="item" container="body" class="ellipsis">
                {{ item
                }}<span
                  *ngIf="quickApplyNotesRequiredSet.has(item)"
                  class="notes-required-diamond"
                >
                  ◆</span
                >
              </div>
            </ng-template>
          </ng-select>
        </nf-form-control>

        <nf-form-control
          *ngIf="quickApplyDescriptionOptions().length > 0"
          label="Description"
          compact
        >
          <ng-select
            [items]="quickApplyDescriptionOptions()"
            placeholder="Apply description to all groups"
            formControlName="quickApplyDescription"
            [closeOnSelect]="true"
            data-cy="quick-apply-description"
            (change)="applyQuickDescription($event)"
          >
            <ng-template ng-option-tmp let-item="item">
              <div [ngbTooltip]="item" container="body" class="ellipsis">
                {{ item }}
              </div>
            </ng-template>
          </ng-select>
        </nf-form-control>
      </div>

      <nf-form-control label="Note" compact>
        <textarea
          class="form-control"
          formControlName="quickApplyNote"
          rows="2"
          placeholder="Apply note to all groups"
          data-cy="quick-apply-note"
        ></textarea>
      </nf-form-control>

      <div
        *ngIf="
          quickApplyActionOptions().length === 0 &&
          quickApplyDescriptionOptions().length === 0
        "
        class="quick-apply-empty"
        data-cy="quick-apply-empty"
      >
        No codes are shared across all selected break types — set codes on each
        group below.
      </div>

      <div
        *ngIf="quickApplyFeedback"
        class="quick-apply-feedback"
        data-cy="quick-apply-feedback"
      >
        {{ quickApplyFeedback }}
      </div>
    </div>

    <!-- One row per break-type group -->
    <div class="section-label">Action &amp; Description by Error Type</div>
    <div class="groups-list">
      <hs-bulk-break-group
        *ngFor="let group of groups"
        [group]="group"
        [keepMode]="keepMode"
        [mode]="mode"
        [saveState]="groupSaveStates.get(group.breakType) || 'idle'"
        [expanded]="isExpanded(group.breakType)"
        [prefillNote]="!appendNotes"
        (toggle)="onToggleGroup(group.breakType)"
        (selectionChange)="onGroupSelectionChange(group.breakType, $event)"
      ></hs-bulk-break-group>
    </div>
  </div>

  <div class="modal-footer" hsDraggable="modal-dialog">
    <span *ngIf="hasRestrictedBreaks" class="restricted-break-msg">
      This is a restricted action that requires approval. Once submitted, the
      pending commands will be found on the Approvals panel for review.
    </span>
    <span *ngIf="saveDisabledReason" class="save-disabled-reason">
      {{ saveDisabledReason }}
    </span>
    <button nf-button="cancel" (click)="onCancel()"></button>
    <span
      [ngbTooltip]="saveDisabledReason"
      container="body"
      class="d-inline-block"
    >
      <button
        nf-button="submit"
        data-cy="bulk-save"
        [loading]="saving"
        [disabled]="form.invalid || validation.submitDisabled || saving"
      >
        {{ submitBtnLabel }}
      </button>
    </span>
  </div>
</form>
